import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ControlCuttysarkSessionRequest,
  CreateCuttysarkSessionRequest,
  CuttysarkLiveView,
  PutCuttysarkBallotRequest,
  PutCuttysarkCandidatePhotoRequest,
  UpdateCuttysarkSessionRequest,
} from '@cortex/domain-product-innovation-lab-types';
import { cuttysarkApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * 進行状態を読み直す間隔（ミリ秒）。
 *
 * PI Lab には push の仕組みが無いため、ステージ画面・参加者画面・運営画面は軽い live
 * エンドポイントを短い間隔で読み直す。タブが裏に回っている間は TanStack Query の既定
 * （refetchIntervalInBackground=false）で止まる。
 * @graph-connects none
 */
export const CUTTYSARK_LIVE_POLL_MS = 2000;

/**
 * セッション一覧を取る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞のセッション一覧と作成可否を取得する
 * @graph-connects /cuttysark/sessions [calls] セッション一覧API
 * @graph-connects cuttysarkApi.list [calls] CuttySark APIクライアント呼び出し
 */
export function useCuttysarkSessions() {
  return useQuery({
    queryKey: queryKeys.cuttysark.list(),
    queryFn: () => cuttysarkApi.list(),
  });
}

/**
 * セッションの進行状態を 2 秒ごとに読み直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 閲覧者ごとに絞った CuttySark 賞セッションの進行状態をポーリングで取得する
 * @graph-connects /cuttysark/sessions/{sessionId}/live [calls] 進行状態API
 * @graph-connects cuttysarkApi.live [calls] CuttySark APIクライアント呼び出し
 */
export function useCuttysarkLive(sessionId: string) {
  return useQuery({
    queryKey: queryKeys.cuttysark.live(sessionId),
    queryFn: () => cuttysarkApi.live(sessionId),
    refetchInterval: CUTTYSARK_LIVE_POLL_MS,
  });
}

/**
 * 操作の結果として返ってきた進行状態をキャッシュへ入れる（次のポーリングを待たずに画面へ反映する）。
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
function useApplyLiveView(sessionId: string): (view: CuttysarkLiveView) => void {
  const queryClient = useQueryClient();
  return (view) => {
    queryClient.setQueryData(queryKeys.cuttysark.live(sessionId), view);
  };
}

/**
 * セッションを作る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞のセッション（チーム予選・グループ予選・本戦の 1 回）を作成する
 * @graph-connects /cuttysark/sessions [calls] セッション作成API
 * @graph-connects cuttysarkApi.create [calls] CuttySark APIクライアント呼び出し
 */
export function useCreateCuttysarkSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCuttysarkSessionRequest) => cuttysarkApi.create(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.cuttysark.list() });
    },
  });
}

/**
 * 部門・候補者・参加者・運営の設定を保存する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの設定をリビジョン付きで保存する
 * @graph-connects /cuttysark/sessions/{sessionId} [calls] 設定更新API
 * @graph-connects cuttysarkApi.update [calls] CuttySark APIクライアント呼び出し
 * @graph-connects useApplyLiveView [calls] 保存結果の即時反映
 */
export function useUpdateCuttysarkSession(sessionId: string) {
  const apply = useApplyLiveView(sessionId);
  return useMutation({
    mutationFn: (input: UpdateCuttysarkSessionRequest) => cuttysarkApi.update(sessionId, input),
    onSuccess: apply,
  });
}

/**
 * 進行操作（状態の切り替え・発表者の切り替え・結果のめくり）を行う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの進行を運営が操作する
 * @graph-connects /cuttysark/sessions/{sessionId}/control [calls] 進行操作API
 * @graph-connects cuttysarkApi.control [calls] CuttySark APIクライアント呼び出し
 * @graph-connects useApplyLiveView [calls] 操作結果の即時反映
 */
export function useControlCuttysarkSession(sessionId: string) {
  const apply = useApplyLiveView(sessionId);
  return useMutation({
    mutationFn: (input: ControlCuttysarkSessionRequest) => cuttysarkApi.control(sessionId, input),
    onSuccess: apply,
  });
}

/**
 * 自分の投票を取る（出し直し用に前回の内容をフォームへ戻す）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分が送った CuttySark 賞の採点・投票を取得する
 * @graph-connects /cuttysark/sessions/{sessionId}/ballot/me [calls] 自分の投票取得API
 * @graph-connects cuttysarkApi.myBallot [calls] CuttySark APIクライアント呼び出し
 */
export function useMyCuttysarkBallot(sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.cuttysark.myBallot(sessionId),
    queryFn: () => cuttysarkApi.myBallot(sessionId),
    enabled,
  });
}

/**
 * 終了した回で、自分（候補者）に届いた応援コメントを取る（書き手は伏せてある）。
 * 終了後は変わらないのでポーリングしない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 終了した CuttySark 賞の回で自分への応援コメントを取得する
 * @graph-connects /cuttysark/sessions/{sessionId}/comments/me [calls] 自分への応援コメント取得API
 * @graph-connects cuttysarkApi.myComments [calls] CuttySark APIクライアント呼び出し
 */
export function useMyCuttysarkComments(sessionId: string) {
  return useQuery({
    queryKey: queryKeys.cuttysark.myComments(sessionId),
    queryFn: () => cuttysarkApi.myComments(sessionId),
  });
}

/**
 * 採点・投票を送る（出し直しは全置換）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞の採点・投票を送信する
 * @graph-connects /cuttysark/sessions/{sessionId}/ballot/me [calls] 投票送信API
 * @graph-connects cuttysarkApi.submitBallot [calls] CuttySark APIクライアント呼び出し
 */
export function useSubmitCuttysarkBallot(sessionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PutCuttysarkBallotRequest) => cuttysarkApi.submitBallot(sessionId, input),
    onSuccess: async (result) => {
      queryClient.setQueryData(queryKeys.cuttysark.myBallot(sessionId), result);
      await queryClient.invalidateQueries({ queryKey: queryKeys.cuttysark.live(sessionId) });
    },
  });
}

/**
 * 運営向けの投票明細を取る（誰が誰に入れたか）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの投票明細を運営が確認する
 * @graph-connects /cuttysark/sessions/{sessionId}/ballots [calls] 投票明細API
 * @graph-connects cuttysarkApi.ballots [calls] CuttySark APIクライアント呼び出し
 */
export function useCuttysarkBallots(sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.cuttysark.ballots(sessionId),
    queryFn: () => cuttysarkApi.ballots(sessionId),
    enabled,
    refetchInterval: CUTTYSARK_LIVE_POLL_MS * 5,
  });
}

/**
 * 候補者の写真をアップロードする（運営のみ）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞の候補者の紹介用写真を運営がアップロードする
 * @graph-connects cuttysarkApi.uploadPhoto [calls] CuttySark APIクライアント呼び出し
 * @graph-connects useApplyLiveView [calls] 操作結果の即時反映
 */
export function useUploadCuttysarkPhoto(sessionId: string) {
  const apply = useApplyLiveView(sessionId);
  return useMutation({
    mutationFn: ({ email, input }: { email: string; input: PutCuttysarkCandidatePhotoRequest }) =>
      cuttysarkApi.uploadPhoto(sessionId, email, input),
    onSuccess: apply,
  });
}

/**
 * 候補者の写真を削除してプロフィール写真に戻す（運営のみ）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞の候補者の紹介用写真を運営が削除する
 * @graph-connects cuttysarkApi.deletePhoto [calls] CuttySark APIクライアント呼び出し
 * @graph-connects useApplyLiveView [calls] 操作結果の即時反映
 */
export function useDeleteCuttysarkPhoto(sessionId: string) {
  const apply = useApplyLiveView(sessionId);
  return useMutation({
    mutationFn: (email: string) => cuttysarkApi.deletePhoto(sessionId, email),
    onSuccess: apply,
  });
}
